/**
 * LEGÖRDÜLŐ — SAJÁT, MERT A NATÍV `<select>` TILOS (#26/#28).
 *
 * ═══ MIÉRT NEM `<select>` ═══
 *
 * A D9 kifejezett tilalma, a `CommandPalette` fejléce idézi is: „billentyűvel
 * csapda: megnyílik és elnyeli a `J`/`K`-t". A natív legördülő megnyitva
 * elveszi a leütéseket a felülettől — egy billentyűzet-első terméken ez nem
 * stíluskérdés. Ezen felül a böngésző a saját felületét rajzolja: a bordó
 * paletta közepén az OS kék kijelölése jelent meg (mérve 2026-09-14, #26).
 *
 * ═══ A RÁDIÓGOMB VISZONT NATÍV MARAD ═══
 *
 * Nem következetlenség. A rádiócsoport NEM NYÍLIK MEG, tehát nem nyel el
 * semmit; a natív viselkedése (nyilak lépkednek, a csoport egy Tab-megálló)
 * pont az, amit kézzel újra kellene építeni. Ott a megjelenés volt rossz, és
 * azt egy `accent-color` token megoldja — lásd `app.css`.
 *
 * ═══ A FÓKUSZ A GOMBON MARAD ═══
 *
 * `aria-activedescendant`: a kijelölés a listán mozog, a fókusz nem. Ugyanaz
 * a szerkezet, mint a parancspalettában — így nem kell a fókuszt a listára
 * költöztetni és záráskor visszahozni, ami a leggyakoribb hibaforrás.
 *
 * ⚠️ AMI SZÁNDÉKOSAN NINCS BENNE: gépelt szűrés. A paletta azért combobox,
 * mert ott sok parancs közül kell találni; egy szerepkör-választón két-három
 * elem áll, és a szűrőmező csak egy üres beviteli helyet ígérne.
 */

import { type ReactElement, useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

export interface SelectOption<T extends string> {
  value: T;
  label: string;
}

interface SelectProps<T extends string> {
  id: string;
  value: T;
  options: readonly SelectOption<T>[];
  onChange: (value: T) => void;
  disabled?: boolean;
}

export function Select<T extends string>({
  id,
  value,
  options,
  onChange,
  disabled,
}: SelectProps<T>): ReactElement {
  const { t } = useTranslation();
  const listId = useId();
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState<T>(value);
  const root = useRef<HTMLDivElement>(null);

  const optionId = (option: T) => `${listId}-${option}`;
  const current = options.find((option) => option.value === value);

  /**
   * KATTINTÁS A LISTÁN KÍVÜLRE ZÁR. A `mousedown`, nem a `click`: utóbbi a
   * gomb saját kattintása UTÁN futna, tehát a nyitó kattintás azonnal be is
   * csukná. (`capture`, hogy a listaelem `onClick`-je előtt fusson.)
   */
  useEffect(() => {
    if (!open) {
      return;
    }
    const close = (event: MouseEvent) => {
      if (!root.current?.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);

  function move(direction: 1 | -1) {
    const index = options.findIndex((option) => option.value === active);
    // KÖRBE NEM FORDUL. Két elemnél a körbefordulás azt a látszatot kelti,
    // hogy a nyíl „nem csinál semmit" — a megállás egyértelműbb.
    const next = Math.min(Math.max(index + direction, 0), options.length - 1);
    setActive(options[next].value);
  }

  function choose(option: T) {
    onChange(option);
    setActive(option);
    setOpen(false);
  }

  function onKeyDown(event: React.KeyboardEvent) {
    switch (event.key) {
      case 'ArrowDown':
      case 'ArrowUp':
        event.preventDefault();
        if (!open) {
          setOpen(true);
          return;
        }
        move(event.key === 'ArrowDown' ? 1 : -1);
        return;
      case 'Enter':
      case ' ':
        event.preventDefault();
        if (open) {
          choose(active);
          return;
        }
        setOpen(true);
        return;
      case 'Escape':
        if (open) {
          // ⚠️ CSAK NYITOTT LISTÁNÁL NYELJÜK EL. Zárt állapotban az `Escape`
          // a KÖRNYEZETÉ — a Beállítások lapon a visszaút, az ablakban a
          // bezárás. Feltétel nélkül elkapva a felhasználó bent ragadna.
          event.preventDefault();
          event.stopPropagation();
          setOpen(false);
        }
        return;
      case 'Tab':
        // A Tab TOVÁBBVISZ, nem választ: a lista becsukódik, az érték marad.
        setOpen(false);
        return;
      default:
        return;
    }
  }

  return (
    <div className="select" ref={root}>
      <button
        type="button"
        id={id}
        className="select__value"
        role="combobox"
        aria-expanded={open}
        aria-controls={listId}
        aria-activedescendant={open ? optionId(active) : undefined}
        disabled={disabled}
        onClick={() => {
          setActive(value);
          setOpen((current) => !current);
        }}
        onKeyDown={onKeyDown}
      >
        {current?.label ?? ''}
        <span className="select__caret" aria-hidden="true">
          ▾
        </span>
      </button>

      {open ? (
        /* ⚠️ `div`, NEM `ul`/`li`. A WAI-ARIA mindkettőt engedi, de a
           `noNoninteractiveElementToInteractiveRole` szabály a listaelemre
           tett `role="option"`-t hibának veszi — a szerep pedig itt NEM
           elhagyható, azon áll az egész minta. A `div` ugyanazt jelenti a
           képernyőolvasónak, és nem kell elnyomást írni alá. */
        <div id={listId} className="select__list" role="listbox" aria-label={t('select.options')}>
          {options.map((option) => (
            // ⚠️ AZ ELEM SZÁNDÉKOSAN NEM FÓKUSZÁLHATÓ — ez maga a minta.
            // Az `aria-activedescendant`-nál a fókusz VÉGIG a comboboxon
            // marad, és a kijelölés mozog a listán; egy `tabIndex` itt
            // pontosan azt rontaná el, amit a szabály védeni akar: minden
            // opció külön Tab-megálló lenne. A billentyűzet-elérés megvan,
            // csak nem az elemeken keresztül.
            // biome-ignore lint/a11y/useFocusableInteractive: aria-activedescendant — a fókusz a comboboxon marad
            <div
              key={option.value}
              id={optionId(option.value)}
              role="option"
              aria-selected={option.value === value}
              className={
                option.value === active ? 'select__option select__option--active' : 'select__option'
              }
              // `mousedown`, nem `click`: a gomb `blur`-je előtt fut, tehát a
              // választás akkor is megtörténik, ha a fókusz közben elmozdul.
              // A BILLENTYŰZET NEM INNEN megy: a leütéseket a combobox gombja
              // kapja, és az `aria-activedescendant` mozgatja a kijelölést —
              // ezért nincs (és nem is kell) `onKeyDown` az elemeken.
              onMouseDown={() => choose(option.value)}
            >
              {option.label}
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}
